import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useCreateCustomPlanRequest } from "@/hooks/use-subscription";
import { SALES_BOOKING_LINK } from "@/lib/constants";

interface CustomPlanRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planType?: "B2B" | "B2C";
}

export function CustomPlanRequestDialog({
  open,
  onOpenChange,
  planType,
}: CustomPlanRequestDialogProps) {
  const [warmupsPerDay, setWarmupsPerDay] = useState("");
  const [mailboxes, setMailboxes] = useState("");
  const [inboxPlacementTests, setInboxPlacementTests] = useState("");
  const [emailHealthTests, setEmailHealthTests] = useState("");
  
  const { mutateAsync: createCustomPlanRequest, isPending: isSubmitting } =
    useCreateCustomPlanRequest();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Validate inputs
      if (!warmupsPerDay || !mailboxes || !inboxPlacementTests || !emailHealthTests) {
        toast.error("Please fill in all fields");
        return;
      }

      // Create the request data
      const requestData = {
        warmupsPerDay: parseInt(warmupsPerDay),
        mailboxes: parseInt(mailboxes),
        inboxPlacementTestsPerMonth: parseInt(inboxPlacementTests),
        emailHealthTestsPerMonth: parseInt(emailHealthTests),
      };

      // Submit to API
      await createCustomPlanRequest(requestData);

      toast.success("Custom plan request submitted successfully! Our sales team will contact you soon.");
      
      // Reset form and close dialog
      setWarmupsPerDay("");
      setMailboxes("");
      setInboxPlacementTests("");
      setEmailHealthTests("");
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
      console.error("Error submitting custom plan request:", error);
    }
  };

  const handleCancel = () => {
    setWarmupsPerDay("");
    setMailboxes("");
    setInboxPlacementTests("");
    setEmailHealthTests("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            Request Custom Plan{planType ? ` ${planType}` : ""}
          </DialogTitle>
          <DialogDescription>
            Please provide your requirements and we'll create a custom plan tailored to your needs.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="warmupsPerDay">
                Number of Warmups Per Day <span className="text-red-500">*</span>
              </Label>
              <Input
                id="warmupsPerDay"
                type="number"
                min="1"
                placeholder="e.g., 500"
                value={warmupsPerDay}
                onChange={(e) => setWarmupsPerDay(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="mailboxes">
                Number of Mailboxes <span className="text-red-500">*</span>
              </Label>
              <Input
                id="mailboxes"
                type="number"
                min="1"
                placeholder="e.g., 10"
                value={mailboxes}
                onChange={(e) => setMailboxes(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inboxPlacementTests">
                Inbox Placement Test Credits Per Month <span className="text-red-500">*</span>
              </Label>
              <Input
                id="inboxPlacementTests"
                type="number"
                min="0"
                placeholder="e.g., 50"
                value={inboxPlacementTests}
                onChange={(e) => setInboxPlacementTests(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="emailHealthTests">
                Email Health Test Credits Per Month <span className="text-red-500">*</span>
              </Label>
              <Input
                id="emailHealthTests"
                type="number"
                min="0"
                placeholder="e.g., 100"
                value={emailHealthTests}
                onChange={(e) => setEmailHealthTests(e.target.value)}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DialogFooter className="flex flex-row gap-2 sm:justify-between">
            <div>
              <Button
                type="button"
                variant="default"
                onClick={() => window.open(SALES_BOOKING_LINK, "_blank")}
                disabled={isSubmitting}
              >
                Contact Sales
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

